/**
* @name FiatAccountFormFactory
* @description
* This factory is used to create a new FiatAccountform object.
*/

<template>
  <section class="fiat-account-form-wrapper">
    <div v-if="isFormLoading">Loading...</div>
    <section v-else-if="shouldShowReview">
      <dl v-for="(category, index) in form" :key="`review_cat_${index}`">
        <h3>{{ category.name }}</h3>
        <div v-for="(field, index) in category.fields" :key="`review_field_${index}`">
          <dt>{{ field.label }}</dt>
          <dd>{{ field.value ? field.value : '-' }}</dd>
        </div>
      </dl>
    </section>
    <form v-else @submit.prevent="handleSubmit()">
      <h2>Step 2 of 3 : {{ accType.display }} particulars</h2>
      <fieldset
        v-for="category in form"
        :key="category.name">
        <legend v-if="Object.keys(category.fields).length > 2">{{ category.name }}</legend>
        <label v-for="field in category.fields" :key="field.label" :class="getUiClass(field)">
          {{ field.label }} <span class="req" v-if="field.isRequired">*</span>
          <vs-select v-if="field.type === FIELD_TYPES.SELECT" v-model="field.value" :required="field.isRequired" autocomplete
            :danger="errors[field.name] || (field.isRequired && !field.value && hasBeenSubmitted)" :danger-text="`Please select a ${field.label}`">
            <vs-select-item :key="index + '_' + item.val" :value="item.val" :text="item.display" v-for="(item, index) in field.acceptedValues" />
          </vs-select>
          <input v-else :type="field.type" v-model.trim="field.value" :is-error="field.isRequired && !field.value && hasBeenSubmitted" :placeholder="field.placeholder" />
          <span v-if="field.type !== FIELD_TYPES.SELECT" class="error-text">{{ `Please input a ${field.label}` }}</span>
        </label>
      </fieldset>
    </form>
  </section>
</template>

<script>
import { PAYOUT_ACCOUNT_TYPES, FIAT_ACCOUNT_FIELD_CATEGORIES, FIAT_ACCOUNT_FIELDS, FIELD_TYPES, COUNTRIES, FIAT_ID_TYPE, FIAT_ACCOUNT_OCCUPATION, FIAT_ACCOUNT_BUSINESS_NATURE } from '../constants';
export default {
  name: 'FiatAccountFormFactory',
  props: {
    currency: {
      type: Object,
      default: () => { },
    },
    country: {
      type: Object,
      default: () => { },
    },
    accType: {
      type: Object,
      default: () => PAYOUT_ACCOUNT_TYPES.INDIVIDUAL,
    },
    isBenef: {
      type: Boolean,
      default: true,
    },
    requiredFields: {
      type: Object,
      default: () => { },
    },
    isSubmitRequested: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object,
      default: () => { },
    },
    shouldShowReview: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {},
      FIELD_TYPES: FIELD_TYPES,
      isFormLoading: false,
      hasBeenSubmitted: false,
      sortedKeys: [
        FIAT_ACCOUNT_FIELD_CATEGORIES.INDIVIDUAL_DETAILS,
        FIAT_ACCOUNT_FIELD_CATEGORIES.COMPANY_DETAILS,
        FIAT_ACCOUNT_FIELD_CATEGORIES.IDENTIFICATION,
        FIAT_ACCOUNT_FIELD_CATEGORIES.ADDRESS,
        FIAT_ACCOUNT_FIELD_CATEGORIES.OTHER,
      ],
      sortedAddressFields: [
        FIAT_ACCOUNT_FIELDS.address,
        FIAT_ACCOUNT_FIELDS.city,
        FIAT_ACCOUNT_FIELDS.province_state,
        FIAT_ACCOUNT_FIELDS.zip_code,
        FIAT_ACCOUNT_FIELDS.country_code,
      ],
      sortedIndivDetailsFields: [
        FIAT_ACCOUNT_FIELDS.first_name,
        FIAT_ACCOUNT_FIELDS.last_name,
        FIAT_ACCOUNT_FIELDS.date_of_birth,
        FIAT_ACCOUNT_FIELDS.nationality,
        FIAT_ACCOUNT_FIELDS.gender,
        FIAT_ACCOUNT_FIELDS.occupation,
      ],
      sortedCompanyDetailsFields: [
        FIAT_ACCOUNT_FIELDS.registered_name,
        FIAT_ACCOUNT_FIELDS.trading_name,
        FIAT_ACCOUNT_FIELDS.registration_type,
        FIAT_ACCOUNT_FIELDS.registration_number,
        FIAT_ACCOUNT_FIELDS.registration_date,
        FIAT_ACCOUNT_FIELDS.registration_expiry_date,
        FIAT_ACCOUNT_FIELDS.registration_country,
        FIAT_ACCOUNT_FIELDS.business_nature,
      ],
    };
  },
  created() {
    this.loadJsonData();
  },

  methods: {
    handleSubmit() {
      // remove categories to go back to flat architecture
      const flatForm = [];
      for (let category of Object.values(this.form)) {
        for (let field of Object.values(category.fields)) {
          flatForm.push({
            name: field.name,
            value: field.value,
            isRequired: field.isRequired,
          });
        }
      }
      this.hasBeenSubmitted = true;
      this.$emit('submitted', flatForm);
    },

    loadJsonData() {
      try {
        this.isFormLoading = true;
        if (this.requiredFields) {
          this.form = {};
          this.createFormFromJson(this.requiredFields);
        }
        this.addNonMandatoryFields();
      } catch (error) {
        this.isFormLoading = false;
        this.form = {};
        console.error('Failed to load JSON data:', error);
      }
    },

    createFormFromJson: function (fields) {
      const targetKey = this.isBenef ? this.accType.value + '_' + 'recipient' : this.accType.value + '_' + 'sender';
      if (!fields[targetKey]) {
        this.isFormLoading = false;
        return this.form;
      }
      for (let field of fields[targetKey]) {
        // Find which category this field belongs to, ie address / additional info / particulars  
        // Push in the formatted field object
        if (this.isIgnoredField(field.field)) {
          continue;
        } else {
          this.addToForm(
            this.getCategory(field.field),
            this.fieldConstructor(field)
          );
        }
      }
      this.sortCategories();
      this.isFormLoading = false;
    },

    addToForm: function (categoryName, toAdd) {
      if (this.form[categoryName]) {
        // make category an object to check whether the field already exists
        this.form[categoryName].fields.push(toAdd);
      } else {
        this.form[categoryName] = {
          name: categoryName,
          fields: [toAdd]
        };
      }
    },

    addNonMandatoryFields() {
      // Add the non mandatory fields
      const nonMandatoryFields = [
        {
          field: 'external_id', // have to keep the same property name as in the json
          ...FIAT_ACCOUNT_FIELDS.external_id
        },
      ];
      for (let field of nonMandatoryFields) {
        this.addToForm(
          this.getCategory(field.field),
          this.fieldConstructor(field)
        );
      }

    },

    getCategory(fieldName) {
      const presetVal = FIAT_ACCOUNT_FIELDS[fieldName].category;
      if (presetVal) {
        return presetVal;
      } else {
        return FIAT_ACCOUNT_FIELD_CATEGORIES.ADDITIONAL_INFO;
      }
    },

    sortCategories() {
      // Sort the categories
      const sortedCategories = {};
      for (let key of this.sortedKeys) {
        if (this.form[key]) {
          sortedCategories[key] = {}; // this.form[key];
          // sort this.form.key depending on the property orderNumber
          sortedCategories[key].fields = Object.values(this.form[key].fields).sort((a, b) => {
            return FIAT_ACCOUNT_FIELDS[a.name].order - FIAT_ACCOUNT_FIELDS[b.name].order;
          });
        }
      }
      this.form = JSON.parse(JSON.stringify(sortedCategories)); // deep copy
    },

    fieldConstructor: function (field) {
      return {
        name: field.field,
        acceptedValues: this.getAcceptedValues(field),
        label: this.translateName(field.field),
        type: this.translateFieldType(field),
        value: this.getDefaultValue(field),
        placeholder: this.getPlaceholder(field.field),
        isRequired: !field.isOptional,
      };
    },

    /**
     * transform the accepted Values into a 
     * @param {any} field 
     */
    getAcceptedValues(field) {
      let accVals = field.accepted_values || [];
      // Exceptonal accepted values for country : If its a residence / registration country / nationality then we put all countries
      // if its for the bank country, then it is limited to the list of countries transmited by the parent component
      if (field.field === 'country_code' || field.field === 'nationality' || field.field === 'identification_issuer' || field.field === 'registration_country') {
        accVals = COUNTRIES.map(country => {
          return {
            val: country.code3,
            display: country.name,
          };
        });
      }
      else if (field.field === 'gender') {
        accVals = [
          {
            val: 'male',
            display: 'Male'
          },
          {
            val: 'female',
            display: 'Female'
          }
        ];
      }
      else if (field.field === 'type') {
        accVals = [
          {
            val: 'bank_account',
            display: 'Bank Account'
          }
        ];
      }
      else if (field.field === 'occupation') {
        accVals = Object.values(FIAT_ACCOUNT_OCCUPATION);
      }
      else if (field.field === 'business_nature') {
        accVals = Object.values(FIAT_ACCOUNT_BUSINESS_NATURE);
      }
      else if (field.field === 'identification_type') {
        // if there is/are value(s) from BE, use the list from BE
        if (accVals.length > 0) {
          accVals = accVals.map(val => {
            return {
              val: FIAT_ID_TYPE[val].value,
              display: FIAT_ID_TYPE[val].display
            };
          });
        } else {
          accVals = Object.values(FIAT_ID_TYPE).map(type => {
            return {
              val: type.value,
              display: type.display,
            };
          });
        }
      }
      else {
        accVals = accVals.map(val => {
          return {
            val: val,
            display: val.replaceAll('_', ' ')
          };
        });
      }

      return accVals;
    },

    /**
     * Sets the field type depending on name / accepted values
     * @param {*} field 
     */
    translateFieldType: function (field) {
      if (field.accepted_values && field.accepted_values.length > 0) {
        return FIELD_TYPES.SELECT;
      } else if (FIAT_ACCOUNT_FIELDS[field.field].type) {
        // Define the type of the input in the dictionary directly, instead of js logic
        return FIAT_ACCOUNT_FIELDS[field.field].type;
      } else {
        return FIELD_TYPES.TEXT;
      }
    },

    translateName: function (name) {
      const presetVal = FIAT_ACCOUNT_FIELDS[name].name;
      if (presetVal) {
        if (presetVal === FIAT_ACCOUNT_FIELDS.country_code.name) {
          if (this.accType.value === PAYOUT_ACCOUNT_TYPES.INDIVIDUAL.value) {
            return 'Country of Residence';
          } else {
            return 'Country of Incorporation';
          }
        }
        return presetVal;
      }
      return name.replace('_', ' ');
    },

    /**
     * Depending on the field name, set the default value
     * @param {*} field 
     */
    getDefaultValue: function (field) {
      field.value = '';
      if (field.field === 'country_code' || field.field === 'registration_country') {
        return this.country.code;
      }
    },

    getPlaceholder: function (name) {
      const presetVal = FIAT_ACCOUNT_FIELDS[name].placeholder;
      if (presetVal) {
        return presetVal;
      }
      return 'Enter your ' + this.translateName(name);
    },

    isIgnoredField: function (field) {
      if (field === FIAT_ACCOUNT_FIELDS.country_code.backend_name) {
        return this.isRegistrationCountryRequired();
      }
      switch (field) {
        case FIAT_ACCOUNT_FIELDS.role.backend_name:
        case FIAT_ACCOUNT_FIELDS.alias.backend_name:
        case FIAT_ACCOUNT_FIELDS.remarks.backend_name:
          return true;
        default:
          return false;
      }
    },

    isRegistrationCountryRequired() {
      // if both country_code and registration_country are required, we ignore the country_code
      // and we will always send duplicate value of the displayed country field
      // ex : if we only dispay country_code, we will still send registration_country = country_code to the BE and vice-versa
      return this.requiredFields[this.accType.value + '_recipient'].find(f => f.field === FIAT_ACCOUNT_FIELDS.registration_country.backend_name);
    },

    getUiClass(field) {
      switch (field.name) {
        case FIAT_ACCOUNT_FIELDS.zip_code.backend_name:
        case FIAT_ACCOUNT_FIELDS.mobile_number.backend_name:
        case FIAT_ACCOUNT_FIELDS.date_of_birth.backend_name:
        case FIAT_ACCOUNT_FIELDS.identification_issue_date.backend_name:
        case FIAT_ACCOUNT_FIELDS.identification_expiry_date.backend_name:
        case FIAT_ACCOUNT_FIELDS.registration_date.backend_name:
        case FIAT_ACCOUNT_FIELDS.registration_expiry_date.backend_name:
        case FIAT_ACCOUNT_FIELDS.gender.backend_name:
        case FIAT_ACCOUNT_FIELDS.identification_type.backend_name:
        case FIAT_ACCOUNT_FIELDS.phone_number.backend_name:
          return 'small';
        case FIAT_ACCOUNT_FIELDS.address.backend_name:
        case FIAT_ACCOUNT_FIELDS.registered_name.backend_name:
        case FIAT_ACCOUNT_FIELDS.trading_name.backend_name:
          return 'full';
        default:
          return '';
      }
    },
  },
  watch: {
    currency() {
      this.loadJsonData();
    },
    country() {
      this.loadJsonData();
    },
    accType() {
      this.loadJsonData();
    },
    requiredFields() {
      this.loadJsonData();
    },
    isSubmitRequested(newVal) {
      if (newVal === true) {
        this.handleSubmit();
      }
    },
  }
};
</script>

<style scoped lang="scss">
section.fiat-account-form-wrapper {
  form {
    max-width: 800px;

    &>h2 {
      font-weight: bold;
      margin-top: $margin-l;
      display: block;
      color: $color-primary-light;
      width: 100%;
      font-size: 14px;
    }

    fieldset {
      border: none;
      margin-top: 0;
      margin-right: 0;
      margin-bottom: $margin-l;
      margin-left: 0;
      display: flex;
      flex-wrap: wrap;
      column-gap: 12px;
      row-gap: 6px;
      align-content: flex-start;

      legend {
        font-weight: bold;
        margin-top: $margin-l;
        display: block;
        width: 100%;
      }

      label {
        position: relative;
        width: calc(50% - 6px);
        min-width: 200px;
        text-transform: capitalize;

        &>* {
          width: 100%;
        }

        &.small {
          width: 150px;
        }

        &.full {
          width: 100%;
        }

        &.three-quarter {
          width: 75%;
        }

        &>span.req {
          color: red;
          width: 1em;
          margin-left: 3px;
        }

        input[type="file"] {
          &::file-selector-button {
            background-color: $color-primary;
            color: $color-secondary;
            border-radius: 3px;
            border: none;
            text-align: center;
            font-size: 1rem;
            padding: 12px;
            margin-right: 8px;
            transition: 0.1s ease-in-out;
            // min-height: calc(24px + 8px + 8px);

            &:hover {
              background-color: $color-primary-dark;
              cursor: pointer;
            }
          }
        }

        input {
          padding: 9.8px;
          box-sizing: border-box;
          margin: 0px;
          border: 1px solid #ccc;
          border-radius: 4px;
          border-color: rgba(0, 0, 0, 0.2);
          font-size: 14px;
          color: $color-text;

          &+span {
            display: none;
          }

          &[is-error] {
            border: 1px solid rgb(183, 58, 74);

            &+span {
              color: rgb(183, 58, 74);
              display: inline-block;
              font-size: 9.1px;
              line-height: 13.65px;
            }
          }
        }
      }
    }
  }

  dl {
    margin-bottom: $margin-l;

    h3 {
      color: $color-primary-light;
      font-weight: bold;
      font-size: 18px;

      &:first-of-type {
        margin-top: $margin-s;
      }
    }

    div {
      dt {
        color: $color-text-light;
      }

      dd {
        font-weight: bold;
      }
    }
  }
}
</style>
